// MUST run before the first DevExpress component renders. DevExpress uses one
// request manager — configuring both ajaxSetup and fetchSetup throws, so only
// fetchSetup is set here. The dashboard control owns its own transport and is
// wired separately per-instance.
export function setupDevExpressAuth(token: string | null): Promise<void> {
    if (!token) return Promise.resolve();
    return import('@devexpress/analytics-core/analytics-utils').then(({ fetchSetup }) => {
        fetchSetup.fetchSettings = { headers: { Authorization: `Bearer ${token}` } };
    });
}

// Safety net for DevExpress transports that ignore per-control header options
// (the dashboard control's item-data XHRs went out anonymous despite headers
// being set). Patches XMLHttpRequest + fetch to add Authorization for backend-
// origin requests only. MUST run before bootstrap.
export function setupBackendAuthNet(token: string | null, apiBaseUrl: string): void {
    if (!token || !apiBaseUrl) return;
    const auth = `Bearer ${token}`;
    const isBackend = (url: string) =>
        url.startsWith(apiBaseUrl) || url.startsWith(apiBaseUrl.replace(/^https?:/, ''));

    // XHR combines duplicate headers ("Bearer X, Bearer X" fails JWT validation),
    // so add Authorization only when no caller has already set it (tracked via the
    // setRequestHeader wrapper below).
    const openOrig = XMLHttpRequest.prototype.open;
    const sendOrig = XMLHttpRequest.prototype.send;
    const setHeaderOrig = XMLHttpRequest.prototype.setRequestHeader;
    type JfXhr = XMLHttpRequest & { __jfBackend?: boolean; __jfAuthSet?: boolean };
    XMLHttpRequest.prototype.open = function (
        this: JfXhr,
        method: string,
        url: string | URL,
        ...rest: never[]
    ) {
        this.__jfBackend = isBackend(String(url));
        this.__jfAuthSet = false;
        return (openOrig as (this: XMLHttpRequest, ...args: unknown[]) => void).call(
            this,
            method,
            url,
            ...(rest.length ? rest : [true]),
        );
    };
    XMLHttpRequest.prototype.setRequestHeader = function (
        this: JfXhr,
        name: string,
        value: string,
    ) {
        if (name.toLowerCase() === 'authorization') {
            this.__jfAuthSet = true;
        }
        return setHeaderOrig.call(this, name, value);
    };
    XMLHttpRequest.prototype.send = function (
        this: JfXhr,
        body?: Document | XMLHttpRequestBodyInit | null,
    ) {
        if (this.__jfBackend && !this.__jfAuthSet) {
            try {
                setHeaderOrig.call(this, 'Authorization', auth);
                this.__jfAuthSet = true;
            } catch {
                /* leave request untouched */
            }
        }
        return sendOrig.call(this, body);
    };

    const fetchOrig = window.fetch.bind(window);
    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
        const url =
            typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
        if (isBackend(url)) {
            const headers = new Headers(
                init?.headers ?? (input instanceof Request ? input.headers : undefined),
            );
            if (!headers.has('Authorization')) {
                headers.set('Authorization', auth);
            }
            return fetchOrig(input, { ...init, headers });
        }
        return fetchOrig(input, init);
    };
}
